"use client";

import { domAnimation, LazyMotion, m } from "framer-motion";
import { Eye, Play, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { MagneticButton } from "@/components/custom/magnetic_button";
import { Squares } from "@/components/custom/squares_background";
import { YouTubeThumbnail } from "@/components/custom/youtube_thumbnail";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { buildHomePricingHref } from "@/lib/pricing-navigation";
import { navigate } from "@/lib/utils";

interface PortfolioItem {
	id: string;
	title: string;
	views: string;
	currentViews: string;
}

const PORTFOLIO_CARD_REVEAL_VIEWPORT = {
	amount: 0.1,
	margin: "0px 0px 120px 0px",
	once: true,
} as const;

const getPortfolioCardRevealDelay = (index: number) =>
	Math.min(index * 0.03, 0.18);

const portfolioStats = [
	{ label: "Campaign Views", value: "50M+" },
	{ label: "Featured Campaigns", value: "250+" },
	{ label: "Completion Rate", value: "98%" },
];

export default function PortfolioClient() {
	const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		let isMounted = true;
		const controller = new AbortController();

		const fetchPortfolio = async () => {
			try {
				const response = await fetch("/api/portfolio", {
					signal: controller.signal,
				});
				if (response.ok) {
					const data = await response.json();
					if (!isMounted) return;
					setPortfolioItems(data);
				}
			} catch (error) {
				if (!controller.signal.aborted) {
					console.error("Failed to fetch portfolio:", error);
				}
			} finally {
				if (isMounted) setIsLoading(false);
			}
		};
		fetchPortfolio();

		return () => {
			isMounted = false;
			controller.abort();
		};
	}, []);

	return (
		<LazyMotion features={domAnimation}>
			<div className="portfolio-page bg-white min-h-screen">
				{/* Creative Hero Section - extends behind navbar */}
				<section className="relative pb-32 pt-52 md:pb-40 md:pt-60 md:min-h-200 bg-zinc-950 text-white overflow-hidden -mt-20">
					<div className="absolute inset-0 z-0">
						<div className="absolute top-0 right-0 w-150 h-150 bg-red-600/10 rounded-full blur-[150px]" />
						<Squares
							theme="dark"
							direction="diagonal"
							speed={0.2}
							squareSize={50}
							className="opacity-20"
							hoverFillColor="#ef4444"
						/>
					</div>

					<div className="container mx-auto px-4 relative z-10">
						<div className="flex flex-col lg:flex-row items-end justify-between gap-12">
							<m.div
								className="lg:w-2/3"
								initial={{ opacity: 0, y: 50 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.8 }}
							>
								<h1 className="font-syne font-bold uppercase leading-[0.85] tracking-tighter mb-8">
									<span className="block text-[18vw] md:text-[7rem] text-zinc-800">
										Proven
									</span>
									<span className="block text-[12vw] md:text-[7rem] text-white">
										Results
									</span>
								</h1>
								<p className="text-md md:text-lg text-justify text-zinc-400 font-inter max-w-xl leading-relaxed">
									We let our numbers speak for themselves. Explore campaigns
									that generated millions of views for creators like you. Each
									project showcases real results achieved through targeted
									Google Ads promotion, delivering authentic engagement from
									genuine viewers worldwide.
								</p>

								<m.div
									className="mt-10"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{ delay: 0.5 }}
								>
									<MagneticButton
										onClick={() =>
											navigate(buildHomePricingHref({ tab: "reach" }))
										}
									>
										<div className="h-14 px-10 bg-red-600 text-white rounded-xl font-syne font-bold uppercase tracking-wide flex items-center justify-center hover:bg-red-500 transition-colors shadow-lg shadow-red-900/40">
											Start Your Campaign
										</div>
									</MagneticButton>
								</m.div>
							</m.div>

							<m.div
								className="lg:w-1/3 w-full"
								initial={{ opacity: 0, x: 20 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ delay: 0.3, duration: 0.8 }}
							>
								<div className="grid grid-cols-1 gap-4">
									{portfolioStats.map((stat) => (
										<div
											key={stat.label}
											className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm flex items-center justify-between hover:bg-white/10 transition-colors duration-300"
										>
											<span className="text-zinc-400 font-inter uppercase tracking-wide text-sm">
												{stat.label}
											</span>
											<span className="text-4xl font-syne font-bold text-white">
												{stat.value}
											</span>
										</div>
									))}
								</div>
							</m.div>
						</div>
					</div>
				</section>

				{/* Portfolio Grid */}
				<section className="py-24 bg-white">
					<div className="container mx-auto px-4">
						{isLoading ? (
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
								{[...Array(8)].map((_, i) => (
									<div
										// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton array
										key={`portfolio-skeleton-${i}`}
										className="animate-pulse"
									>
										<div className="aspect-video bg-zinc-200 rounded-t-[1.325rem]" />
										<div className="p-6 bg-zinc-50 rounded-b-[1.325rem]">
											<div className="flex gap-2 mb-4">
												<div className="h-6 w-16 bg-zinc-200 rounded" />
												<div className="h-6 w-20 bg-zinc-200 rounded" />
											</div>
											<div className="h-4 w-full bg-zinc-200 rounded mb-2" />
											<div className="h-4 w-2/3 bg-zinc-200 rounded" />
										</div>
									</div>
								))}
							</div>
						) : portfolioItems.length === 0 ? (
							<div className="text-center py-12">
								<p className="text-zinc-500">No portfolio items available.</p>
							</div>
						) : (
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
								{portfolioItems.map((item, index) => (
									<m.div
										key={item.id}
										initial={{ opacity: 0, y: 30 }}
										whileInView={{ opacity: 1, y: 0 }}
										viewport={PORTFOLIO_CARD_REVEAL_VIEWPORT}
										transition={{
											delay: getPortfolioCardRevealDelay(index),
											duration: 0.45,
										}}
									>
										<Card
											className="group w-full overflow-hidden border border-zinc-200 shadow-sm hover:shadow-2xl hover:border-zinc-300 transition-[box-shadow,border-color] duration-500 cursor-pointer pt-0 rounded-[1.325rem] bg-zinc-50"
											onClick={() =>
												window.open(
													`https://www.youtube.com/watch?v=${item.id}`,
													"_blank",
												)
											}
										>
											<div className="relative aspect-video bg-zinc-200 overflow-hidden">
												<YouTubeThumbnail
													videoId={item.id}
													alt={item.title}
													width={480}
													height={270}
													className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
													sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
												/>

												{/* Overlay */}
												<div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
													<div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center scale-75 opacity-0 group-hover:scale-100 group-hover:opacity-100 transition-[transform,opacity] duration-300 border border-white/50">
														<Play className="w-6 h-6 text-white ml-1 fill-white" />
													</div>
												</div>

												{/* Floating Badge */}
												<div className="absolute top-3 right-3">
													<Badge className="bg-primary hover:bg-primary text-white border-none font-syne uppercase tracking-wider text-[10px] px-2.5 py-1 rounded-lg shadow-lg">
														Promoted
													</Badge>
												</div>
											</div>

											<CardContent className="p-6">
												<div className="flex gap-2 mb-4">
													<div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-lg border border-zinc-200 shadow-sm">
														<Eye className="w-3.5 h-3.5 text-zinc-400" />
														<span className="text-xs font-bold font-syne text-zinc-900">
															{item.currentViews}
														</span>
													</div>
													<div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/5 rounded-lg border border-primary/10 shadow-sm">
														<TrendingUp className="w-3.5 h-3.5 text-primary" />
														<span className="text-xs font-bold font-syne text-primary">
															Target: {item.views}
														</span>
													</div>
												</div>

												<h3 className="font-syne font-bold text-base leading-tight text-zinc-900 line-clamp-2 uppercase tracking-tight group-hover:text-primary transition-colors">
													{item.title}
												</h3>
											</CardContent>
										</Card>
									</m.div>
								))}
							</div>
						)}

						<div className="mt-16 text-center border-t border-zinc-100 pt-8">
							<p className="text-sm text-zinc-400 font-inter italic max-w-xl mx-auto">
								*Permission has been obtained from the owners to publish the
								list presented here. Individual results may vary based on
								content quality and niche.
							</p>
						</div>
					</div>
				</section>
			</div>
		</LazyMotion>
	);
}
